import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  recipient_kind: 'client', // 'client' | 'lead'
  client_id: '', lead_id: '',
  product_type: 'Term', insurer: '', plan_name: '',
  sum_assured: 0, tenure: 10, premium_amount: 0,
  net_premium: '', gst_amount: '', total_premium: '',
  status: 'Draft',
};

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [qRes, cRes, lRes] = await Promise.all([
        axios.get(`${API}/quotations`, { withCredentials: true }),
        axios.get(`${API}/clients`,    { withCredentials: true }),
        axios.get(`${API}/leads`,      { withCredentials: true }),
      ]);
      setQuotations(qRes.data);
      setClients(cRes.data);
      setLeads(lRes.data);
    } catch (error) { toast.error('Failed to load quotations'); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.recipient_kind === 'client' && !formData.client_id) { toast.error('Please pick a client'); return; }
    if (formData.recipient_kind === 'lead' && !formData.lead_id) { toast.error('Please pick a lead'); return; }
    try {
      const payload = {
        product_type: formData.product_type, insurer: formData.insurer, plan_name: formData.plan_name,
        sum_assured: formData.sum_assured, tenure: formData.tenure, premium_amount: formData.premium_amount,
        net_premium: formData.net_premium === '' ? null : parseFloat(formData.net_premium),
        gst_amount:  formData.gst_amount === '' ? null : parseFloat(formData.gst_amount),
        total_premium: formData.total_premium === '' ? null : parseFloat(formData.total_premium),
        status: formData.status,
        ...(formData.recipient_kind === 'client' ? { client_id: formData.client_id } : { lead_id: formData.lead_id }),
      };
      await axios.post(`${API}/quotations`, payload, { withCredentials: true });
      toast.success('Quotation created');
      setDialogOpen(false);
      setFormData({ ...emptyForm });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save quotation'); }
  };

  const updateStatus = async (quotationId, status) => {
    try {
      await axios.put(`${API}/quotations/${quotationId}`, { status }, { withCredentials: true });
      toast.success('Status updated'); loadData();
    } catch (error) { toast.error('Failed to update'); }
  };

  const getStatusColor = (status) => ({
    'Draft': 'bg-slate-100 text-slate-800', 'Sent': 'bg-blue-100 text-blue-800',
    'Accepted': 'bg-green-100 text-green-800', 'Rejected': 'bg-red-100 text-red-800'
  }[status] || 'bg-slate-100 text-slate-800');

  const downloadPDF = async (quotationId, planName) => {
    try {
      const res = await axios.get(`${API}/quotations/${quotationId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${(planName || 'insuregrowth').replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('Failed to download PDF'); }
  };

  const gstAutoCalc = (net, gst) => {
    const n = parseFloat(net) || 0; const g = parseFloat(gst) || 0;
    return (n + g).toFixed(2);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Quotations</h1>
          <p className="text-slate-600 mt-1">Create quotations for clients or leads and send as PDF</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-quotation-button"><Plus className="h-4 w-4 mr-2" /> New Quotation</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Quotation</DialogTitle>
              <DialogDescription>Send to an existing client or a lead/prospect who hasn't converted yet.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="quotation-recipient-toggle">
                <button type="button" onClick={() => setFormData({...formData, recipient_kind: 'client', lead_id: ''})} className={`px-3 py-1.5 rounded text-xs font-medium ${formData.recipient_kind === 'client' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600'}`} data-testid="quotation-kind-client">To Client</button>
                <button type="button" onClick={() => setFormData({...formData, recipient_kind: 'lead', client_id: ''})} className={`px-3 py-1.5 rounded text-xs font-medium ${formData.recipient_kind === 'lead' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600'}`} data-testid="quotation-kind-lead">To Lead</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>{formData.recipient_kind === 'client' ? 'Client' : 'Lead'} *</Label>
                  {formData.recipient_kind === 'client' ? (
                    <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                      <SelectTrigger data-testid="quotation-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={formData.lead_id} onValueChange={(v) => setFormData({...formData, lead_id: v})}>
                      <SelectTrigger data-testid="quotation-lead-select"><SelectValue placeholder="Select lead" /></SelectTrigger>
                      <SelectContent>{leads.map(l => <SelectItem key={l.lead_id} value={l.lead_id}>{l.name} · {l.phone}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2"><Label>Product Type</Label>
                  <Select value={formData.product_type} onValueChange={(v) => setFormData({...formData, product_type: v})}>
                    <SelectTrigger data-testid="quotation-product-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['Term','Endowment','ULIP','Whole Life','Health','Motor','Travel','Home','Personal Accident','Commercial'].map(pt => <SelectItem key={pt} value={pt}>{pt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Insurer *</Label><Input value={formData.insurer} onChange={(e) => setFormData({...formData, insurer: e.target.value})} required data-testid="quotation-insurer-input" /></div>
                <div className="space-y-2"><Label>Plan Name *</Label><Input value={formData.plan_name} onChange={(e) => setFormData({...formData, plan_name: e.target.value})} required data-testid="quotation-plan-input" /></div>
                <div className="space-y-2"><Label>Sum Assured (₹)</Label><Input type="number" value={formData.sum_assured} onChange={(e) => setFormData({...formData, sum_assured: parseFloat(e.target.value) || 0})} data-testid="quotation-sum-input" /></div>
                <div className="space-y-2"><Label>Tenure (Years)</Label><Input type="number" value={formData.tenure} onChange={(e) => setFormData({...formData, tenure: parseInt(e.target.value, 10) || 0})} data-testid="quotation-tenure-input" /></div>
                <div className="space-y-2"><Label>Premium (₹) *</Label><Input type="number" value={formData.premium_amount} onChange={(e) => setFormData({...formData, premium_amount: parseFloat(e.target.value) || 0})} required data-testid="quotation-premium-input" /></div>
                <div className="space-y-2"><Label>Net Premium (₹)</Label><Input type="number" value={formData.net_premium} onChange={(e) => { const n = e.target.value; setFormData({...formData, net_premium: n, total_premium: gstAutoCalc(n, formData.gst_amount)}); }} placeholder="Before GST" data-testid="quotation-net-premium-input" /></div>
                <div className="space-y-2"><Label>GST (₹)</Label><Input type="number" value={formData.gst_amount} onChange={(e) => { const g = e.target.value; setFormData({...formData, gst_amount: g, total_premium: gstAutoCalc(formData.net_premium, g)}); }} data-testid="quotation-gst-input" /></div>
                <div className="col-span-2 space-y-2"><Label>Total Premium (₹)</Label><Input type="number" value={formData.total_premium} onChange={(e) => setFormData({...formData, total_premium: e.target.value})} placeholder="Auto: Net + GST (editable)" data-testid="quotation-total-premium-input" /></div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="quotation-submit-button">Create Quotation</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>All Quotations ({quotations.length})</CardTitle></CardHeader>
        <CardContent>
          {quotations.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No quotations yet. Create your first quotation.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Recipient</TableHead><TableHead>Kind</TableHead><TableHead>Product</TableHead><TableHead>Insurer</TableHead><TableHead>Sum Assured</TableHead><TableHead>Premium</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {quotations.map((q) => (
                  <TableRow key={q.quotation_id} data-testid={`quotation-row-${q.quotation_id}`}>
                    <TableCell className="font-medium">{q.recipient_name || '-'}</TableCell>
                    <TableCell><Badge className={q.recipient_kind === 'lead' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>{q.recipient_kind === 'lead' ? 'Lead' : 'Client'}</Badge></TableCell>
                    <TableCell>{q.product_type}</TableCell>
                    <TableCell>{q.insurer}</TableCell>
                    <TableCell>{formatCurrency(q.sum_assured)}</TableCell>
                    <TableCell>{formatCurrency(q.premium_amount)}</TableCell>
                    <TableCell>{q.total_premium ? formatCurrency(q.total_premium) : '-'}</TableCell>
                    <TableCell>
                      <Select value={q.status} onValueChange={(v) => updateStatus(q.quotation_id, v)}>
                        <SelectTrigger className="w-32"><Badge className={getStatusColor(q.status)}>{q.status}</Badge></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Draft">Draft</SelectItem>
                          <SelectItem value="Sent">Sent</SelectItem>
                          <SelectItem value="Accepted">Accepted</SelectItem>
                          <SelectItem value="Rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => downloadPDF(q.quotation_id, q.plan_name)} data-testid={`download-quotation-${q.quotation_id}`} title="Download PDF">
                        <Download className="h-4 w-4 text-blue-700" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Quotations;
